import React from "react";
import { Admin, DashboardComponent } from "react-admin";
import { useDispatch } from "react-redux";
import { History } from "history";
import { I18nProvider } from "react-admin";
import { ApiManager } from "../data/ApiManager";
import { ThemeOptions } from "@material-ui/core";
import { ProLogsPermissions } from "../auth/permissions";
import { getAdminResources } from "./adminResources";
import { useEffect } from "react";
import { AuthManager } from "../auth/Auth";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { createSetFleetManagerHubAction } from "../redux/actions/fleetManagerHub";
import { Dashboard } from "../../components/dashboard/Dashboard";
import { MaintenanceDashboardSelectedVehiclesContext } from "../../components/assets/maintenance-dashboard/MaintenanceDashboardSelectedVehiclesContext";
import { SafetyDashboardSelectedDriversContext } from "../../components/safety/safety-dashboard/SafetyDashboardSelectedDriversContext";
import { ProLogsThemeProvider } from "../../components/layout/ThemeProvider";
import { AppBarFiltersContainerContextProvider } from "../../components/common/app-bar-filters/AppBarFiltersContainerContextProvider";
import { useLogoutWhenInactive } from "../hooks/useLogoutWhenInactive";

interface IAdminWrapperProps {
  history: History<{}>;
  dataProvider: ApiManager;
  authProvider: AuthManager;
  i18nProvider: I18nProvider;
  loginPage: (props: any) => JSX.Element;
  layout: (props: any) => JSX.Element;
  dashboard?: DashboardComponent;
  customRoutes: JSX.Element[];
  theme?: ThemeOptions;
}

export const AdminWrapper = (props: IAdminWrapperProps) => {
  const { dataProvider, authProvider, history } = props;
  const dispatch = useDispatch();

  React.useLayoutEffect(() => {
    authProvider.init({
      clientId: process.env["REACT_APP_AUTH_CLIENT_ID"] ?? "",
      authority: process.env["REACT_APP_AUTH_AUTHORITY"] ?? "",
      redirectUri: process.env["REACT_APP_AUTH_REDIRECT_URI"] ?? "",
      silentRedirectUri:
        process.env["REACT_APP_AUTH_SILENT_REDIRECT_URI"] ?? "",
      postLogoutRedirectUri:
        process.env["REACT_APP_AUTH_POST_LOGOUT_REDIRECT_URI"] ?? "",
    });

    dataProvider.init(dispatch);
  }, [dataProvider, authProvider, dispatch]);

  useLogoutWhenInactive(authProvider, history);
  useEffect(() => {
    let hubConnection: HubConnection | undefined;
    authProvider.checkAuth(null).then(() => {
      const apiUrl =
        process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
        "https://localhost:44327";

      hubConnection = new HubConnectionBuilder()
        .withUrl(`${apiUrl}/signalr-hubs/fleet-manager-hub`, {
          accessTokenFactory: authProvider.getAccessToken,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.elapsedMilliseconds < 5 * 60 * 1000) {
              // If we've been reconnecting for less than 5 minutes so far,
              // wait between 0 and 10 seconds before the next reconnect attempt.
              console.log("Attempt to reconnect to the hub...");
              return Math.random() * 10000;
            } else {
              // If we've been reconnecting for more than 5 minutes so far, stop reconnecting.
              console.warn(
                "Failed to reconnect to the hub for more than 5 minutes, aborting."
              );
              return null;
            }
          },
        })
        .configureLogging(LogLevel.Information)
        .build();

      hubConnection.on("OnPasswordChanged", () => history.push("/logout"));

      hubConnection.start();
      dispatch(createSetFleetManagerHubAction({ hub: hubConnection }));
    });

    return () => {
      if (hubConnection) {
        dispatch(createSetFleetManagerHubAction({}));
        hubConnection.off("OnPasswordChanged");
        hubConnection.stop();
      }
    };
  }, [authProvider, dispatch, history]);

  return (
    <AppBarFiltersContainerContextProvider>
      <MaintenanceDashboardSelectedVehiclesContext>
        <SafetyDashboardSelectedDriversContext>
          <ProLogsThemeProvider>
            <Admin dashboard={Dashboard} {...props} disableTelemetry>
              {(perms: ProLogsPermissions) => {
                const { actionPermissions: permissions } = perms;
                return getAdminResources(permissions);
              }}
            </Admin>
          </ProLogsThemeProvider>
        </SafetyDashboardSelectedDriversContext>
      </MaintenanceDashboardSelectedVehiclesContext>
    </AppBarFiltersContainerContextProvider>
  );
};
